import { LandingHeader } from './LandingHeader'
import { LandingFooter } from './LandingFooter'
import { SvgViewer } from './SvgViewer'
import { GetStarted } from './GetStarted'
import { playgroundUrl } from './playgroundUrl'

const enSource = `customer
do: place order
needs: cart, payment method
yields: order
at: web

fraud service
do: score risk
needs: order, customer history
yields: risk score
at: payments

payment service
do: authorize payment
needs: order
yields: authorized order, charge id
at: payments

payment service
do: capture payment
needs: authorized order, risk score
yields: paid order
at: payments

warehouse
do: pick items
needs: paid order
yields: picked package
at: fulfillment

warehouse
do: ship package
needs: picked package, shipping label
yields: shipped order
at: fulfillment

notifier
do: send receipt
needs: paid order, email address
yields: receipt email
at: comms

support
do: process refund
needs: shipped order, customer complaint, charge id
yields: refunded
at: support`

export function LandingPage() {
    const tryUrl = playgroundUrl

    return (
        <div className="min-h-screen" style={{ scrollBehavior: 'smooth' }}>
            <LandingHeader />

            <main>
                {/* ── Hero ── */}
                <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                    {/* Abstract background: a diagonal accent wash, a fine dot
                        grid, and two blurred accent orbs. No diagram, no
                        content — just texture the headline can sit on. */}
                    <div
                        aria-hidden
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                'linear-gradient(135deg,' +
                                ' color-mix(in srgb, var(--accent) 6%, transparent) 0%,' +
                                ' transparent 45%,' +
                                ' color-mix(in srgb, var(--accent) 4%, transparent) 100%)',
                            zIndex: 0,
                        }}
                    />
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: 'radial-gradient(circle, color-mix(in srgb, var(--accent) 8%, transparent) 1px, transparent 1px)',
                            backgroundSize: '32px 32px',
                            zIndex: 1,
                        }}
                    />
                    <div
                        className="absolute top-[20%] left-[10%] w-[400px] h-[400px] rounded-full blur-[100px]"
                        style={{ background: 'color-mix(in srgb, var(--accent) 4%, transparent)', zIndex: 2 }}
                    />
                    <div
                        className="absolute bottom-[20%] right-[10%] w-[350px] h-[350px] rounded-full blur-[120px]"
                        style={{ background: 'color-mix(in srgb, var(--accent) 3%, transparent)', zIndex: 2 }}
                    />

                    <div className="relative z-10 max-w-6xl w-full mx-auto px-6 pt-24 pb-16 text-center">
                        <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-6">
                            Notation &middot; MCP Tools &middot; Visual Playground
                        </p>
                        <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 text-text">
                            Write structure.
                            <br />
                            <span className="text-accent">Get truth.</span>
                        </h1>
                        <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto mb-10 leading-relaxed">
                            Describe any concurrent system in plain text.
                            Get deterministic structural analysis &mdash; topology, conservation laws,
                            deadlock detection, path tracing. No AI inside the engine.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <a
                                href="#get-started"
                                onClick={(e) => {
                                    e.preventDefault()
                                    document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth' })
                                }}
                                className="bg-accent text-bg font-bold px-8 py-3.5 rounded-full text-sm hover:opacity-90 transition-opacity"
                            >
                                Install MCP Server
                            </a>
                            <a
                                href={tryUrl}
                                className="font-medium px-8 py-3.5 rounded-full text-sm text-text hover:bg-surface transition-colors"
                                style={{ border: '1px solid var(--border)' }}
                            >
                                Try the Playground
                            </a>
                        </div>
                    </div>
                </section>

                {/* ── Demo: EN code + SVG + analysis ── */}
                <section className="py-24 px-6">
                    <div className="max-w-6xl mx-auto">
                        <div className="mb-12">
                            <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">
                                How It Works
                            </p>
                            <h2 className="font-display text-3xl md:text-4xl font-bold text-text">
                                Ask the structure,<br />
                                <span className="text-accent">not the code.</span>
                            </h2>
                            <p className="mt-4 text-text-secondary max-w-2xl leading-relaxed">
                                Every concurrent system has a hidden skeleton &mdash; bridges, bottlenecks,
                                conservation laws, deadlock risks &mdash; that code alone can&rsquo;t
                                reveal. EN extracts it deterministically. Six tools, six questions you
                                couldn&rsquo;t answer any other way. Below is one source, analyzed six
                                ways.
                            </p>
                        </div>

                        <div className="lg:grid lg:grid-cols-2 lg:gap-8">
                            {/* Left: EN code. On desktop the outer panel is
                                pinned to the SVG's 520px so the two bottoms
                                line up; the <pre> scrolls inside. On mobile
                                the panel stacks above the diagram and can
                                grow to content height (no scroll needed). */}
                            <div className="rounded-xl overflow-hidden mb-6 lg:mb-0 flex flex-col lg:h-[520px]" style={{ border: '1px solid var(--border)' }}>
                                <div
                                    className="flex items-center gap-3 px-4 py-2.5 bg-surface-elevated flex-shrink-0"
                                    style={{ borderBottom: '1px solid var(--border-subtle)' }}
                                >
                                    <div className="flex gap-1.5">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--danger) 50%, transparent)' }} />
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--warning) 50%, transparent)' }} />
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--success) 50%, transparent)' }} />
                                    </div>
                                    <span className="text-[11px] font-mono text-text-muted">checkout.en</span>
                                </div>
                                <pre className="p-5 font-mono text-[11px] sm:text-[13px] leading-[1.6] sm:leading-[1.8] flex-1 min-h-0 overflow-auto">
                                    <div className="text-accent font-medium">customer</div>
                                    <div><span className="text-text-secondary">do: </span><span className="text-text">place order</span></div>
                                    <div><span className="text-text-secondary">needs: </span><span className="text-text">cart, payment method</span></div>
                                    <div><span className="text-text-secondary">yields: </span><span className="text-text">order</span></div>
                                    <div><span className="text-text-secondary">at: </span><span className="text-text-muted">web</span></div>
                                    <div className="h-3" />
                                    <div className="text-accent font-medium">fraud service</div>
                                    <div><span className="text-text-secondary">do: </span><span className="text-text">score risk</span></div>
                                    <div><span className="text-text-secondary">needs: </span><span className="text-text">order, customer history</span></div>
                                    <div><span className="text-text-secondary">yields: </span><span className="text-text">risk score</span></div>
                                    <div><span className="text-text-secondary">at: </span><span className="text-text-muted">payments</span></div>
                                    <div className="h-3" />
                                    <div className="text-accent font-medium">payment service</div>
                                    <div><span className="text-text-secondary">do: </span><span className="text-text">authorize payment</span></div>
                                    <div><span className="text-text-secondary">needs: </span><span className="text-text">order</span></div>
                                    <div><span className="text-text-secondary">yields: </span><span className="text-text">authorized order, charge id</span></div>
                                    <div><span className="text-text-secondary">at: </span><span className="text-text-muted">payments</span></div>
                                    <div className="h-3" />
                                    <div className="text-accent font-medium">payment service</div>
                                    <div><span className="text-text-secondary">do: </span><span className="text-text">capture payment</span></div>
                                    <div><span className="text-text-secondary">needs: </span><span className="text-text">authorized order, risk score</span></div>
                                    <div><span className="text-text-secondary">yields: </span><span className="text-text">paid order</span></div>
                                    <div><span className="text-text-secondary">at: </span><span className="text-text-muted">payments</span></div>
                                    <div className="h-3" />
                                    <div className="text-accent font-medium">warehouse</div>
                                    <div><span className="text-text-secondary">do: </span><span className="text-text">pick items</span></div>
                                    <div><span className="text-text-secondary">needs: </span><span className="text-text">paid order</span></div>
                                    <div><span className="text-text-secondary">yields: </span><span className="text-text">picked package</span></div>
                                    <div><span className="text-text-secondary">at: </span><span className="text-text-muted">fulfillment</span></div>
                                    <div className="h-3" />
                                    <div className="text-accent font-medium">warehouse</div>
                                    <div><span className="text-text-secondary">do: </span><span className="text-text">ship package</span></div>
                                    <div><span className="text-text-secondary">needs: </span><span className="text-text">picked package, shipping label</span></div>
                                    <div><span className="text-text-secondary">yields: </span><span className="text-text">shipped order</span></div>
                                    <div><span className="text-text-secondary">at: </span><span className="text-text-muted">fulfillment</span></div>
                                    <div className="h-3" />
                                    <div className="text-accent font-medium">notifier</div>
                                    <div><span className="text-text-secondary">do: </span><span className="text-text">send receipt</span></div>
                                    <div><span className="text-text-secondary">needs: </span><span className="text-text">paid order, email address</span></div>
                                    <div><span className="text-text-secondary">yields: </span><span className="text-text">receipt email</span></div>
                                    <div><span className="text-text-secondary">at: </span><span className="text-text-muted">comms</span></div>
                                    <div className="h-3" />
                                    <div className="text-accent font-medium">support</div>
                                    <div><span className="text-text-secondary">do: </span><span className="text-text">process refund</span></div>
                                    <div><span className="text-text-secondary">needs: </span><span className="text-text">shipped order, customer complaint, charge id</span></div>
                                    <div><span className="text-text-secondary">yields: </span><span className="text-text">refunded</span></div>
                                    <div><span className="text-text-secondary">at: </span><span className="text-text-muted">support</span></div>
                                </pre>
                            </div>

                            {/* Right: SVG diagram */}
                            <SvgViewer
                                src="/hero-diagram.svg"
                                label="Checkout · 8 actions · 5 locations · 4 subsystems discovered"
                                alt="E-commerce checkout structural diagram"
                                maxHeight="520px"
                                enSource={enSource}
                            />
                        </div>

                        {/* Six findings — each card: tool name (code), terse
                            technical sub-line (raw engine output), bold plain-
                            English key takeaway, thin plain-English elaboration.
                            Both body sentences stay plain-English; only the
                            sub-line carries technical notation. */}
                        <div className="mt-8 grid md:grid-cols-2 gap-4">
                            <div className="rounded-lg p-5" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}>
                                <code className="text-xs font-mono text-accent font-bold">structure</code>
                                <p className="text-[11px] font-mono text-text-muted mt-1">7 bridge nodes · 4 auto-discovered subsystems</p>
                                <p className="text-sm text-text mt-2 leading-relaxed">
                                    <span className="font-bold">Finds single points of failure and natural service boundaries.</span>{' '}
                                    <span className="text-text-secondary">Tells you which checks
                                    block 100% of traffic if they go down, and which clusters of
                                    actions could be peeled off as separate services &mdash; no
                                    hints needed.</span>
                                </p>
                            </div>
                            <div className="rounded-lg p-5" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}>
                                <code className="text-xs font-mono text-accent font-bold">invariant</code>
                                <p className="text-[11px] font-mono text-text-muted mt-1">payment method &minus; cart = 0</p>
                                <p className="text-sm text-text mt-2 leading-relaxed">
                                    <span className="font-bold">No order slips through without a payment method.</span>{' '}
                                    <span className="text-text-secondary">One cart in, one payment consumed.
                                    The balance holds no matter what else happens in the system.</span>
                                </p>
                            </div>
                            <div className="rounded-lg p-5" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}>
                                <code className="text-xs font-mono text-accent font-bold">live</code>
                                <p className="text-[11px] font-mono text-text-muted mt-1">siphon: {'{customer complaint}'}</p>
                                <p className="text-sm text-text mt-2 leading-relaxed">
                                    <span className="font-bold">Refunds only fire when customers complain.</span>{' '}
                                    <span className="text-text-secondary">If the complaint channel goes quiet,
                                    the refund flow stops with it. Worth knowing before the next bad
                                    release.</span>
                                </p>
                            </div>
                            <div className="rounded-lg p-5" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}>
                                <code className="text-xs font-mono text-accent font-bold">reachable</code>
                                <p className="text-[11px] font-mono text-text-muted mt-1">cart &rarr; refunded: 6 hops via charge id</p>
                                <p className="text-sm text-text mt-2 leading-relaxed">
                                    <span className="font-bold">A refund can fire without the order ever shipping.</span>{' '}
                                    <span className="text-text-secondary">The shortest path skips warehouse and
                                    courier entirely. Useful when writing &ldquo;can this order still be
                                    cancelled?&rdquo; rules.</span>
                                </p>
                            </div>
                            <div className="rounded-lg p-5" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}>
                                <code className="text-xs font-mono text-accent font-bold">equivalent</code>
                                <p className="text-[11px] font-mono text-text-muted mt-1">compare: with-fraud vs without-fraud &mdash; 6 ops</p>
                                <p className="text-sm text-text mt-2 leading-relaxed">
                                    <span className="font-bold">See what changed before touching production.</span>{' '}
                                    <span className="text-text-secondary">Point it at the old
                                    version and the new one and you get a specific list of what
                                    was removed, added, or rewired &mdash; not a guess about
                                    which downstream flow a change might have broken.</span>
                                </p>
                            </div>
                            <div className="rounded-lg p-5" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}>
                                <code className="text-xs font-mono text-accent font-bold">compose</code>
                                <p className="text-[11px] font-mono text-text-muted mt-1">extract: fulfillment &rarr; standalone EN</p>
                                <p className="text-sm text-text mt-2 leading-relaxed">
                                    <span className="font-bold">Pull any subsystem out as its own service.</span>{' '}
                                    <span className="text-text-secondary">Boundary inputs and outputs are
                                    derived for you. Hand the spec to the team that owns it, no
                                    contract drafting required.</span>
                                </p>
                            </div>
                        </div>

                        <div className="mt-8 text-center">
                            <a href="/examples" className="text-sm font-mono text-accent hover:underline">
                                See all tools in action &rarr;
                            </a>
                        </div>
                    </div>
                </section>

                {/* ── Engine + AI ── */}
                <section className="py-24 px-6" style={{ borderTop: '1px solid color-mix(in srgb, var(--border) 40%, transparent)' }}>
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="font-display text-3xl md:text-4xl font-bold text-text mb-4">
                                The engine computes. AI narrates.
                            </h2>
                            <p className="text-text-secondary max-w-2xl mx-auto leading-relaxed">
                                EN produces structural facts &mdash; topology, bridges, conservation laws, deadlock risks.
                                Your AI agent reads those facts and translates them into your domain.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="rounded-xl p-6" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                                <div className="flex items-center gap-2 mb-4">
                                    <div
                                        className="w-6 h-6 rounded flex items-center justify-center font-mono text-[10px] font-bold text-accent"
                                        style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}
                                    >
                                        EN
                                    </div>
                                    <span className="text-sm font-mono text-text-muted">Engine</span>
                                </div>
                                <pre className="font-mono text-[11px] sm:text-[13px] leading-[1.8] text-text-secondary whitespace-pre-wrap">
{`isStructurallyLive: false
uncoveredSiphons:
  {customer history}
    affectedActors: [fraud engine]

conservationLaws:
  card token + merchant request = const`}
                                </pre>
                            </div>

                            <div className="rounded-xl p-6" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-bg text-[10px] font-bold">
                                        AI
                                    </div>
                                    <span className="text-sm font-mono text-text-muted">Interpretation</span>
                                </div>
                                <div className="text-sm text-text-secondary leading-relaxed space-y-3">
                                    <p>
                                        The fraud engine has a problem: customer history is consumed
                                        but never replenished. Once it runs out, fraud scoring stops permanently.
                                    </p>
                                    <p>
                                        Card tokens and merchant requests are structurally balanced &mdash;
                                        every payment that enters produces exactly one of each.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Get Started ── */}
                <GetStarted />
            </main>

            <LandingFooter />
        </div>
    )
}
